import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { RootState } from 'store';
import { useGetMeQuery } from 'store/services/user/meApi';
import {
  useGetAffiliateDashboardQuery,
  AffiliateDashboardMonth,
} from 'store/services/affiliates/affiliatesApi';
import { getReferralUrl } from 'utils/referralLink';
import { EarningsHistoryTable } from 'screens/Settings/components/AffiliatesTab';
import PromoResources from './PromoResources';
import './AffiliateDashboardPage.css';
import { copyToClipboard } from 'utils/copyToClipboard';

const formatCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const formatMonthTick = (month: string): string => {
  // "2025-08" → "Aug '25"
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  return `${date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} '${year.slice(2)}`;
};

const formatMonthLong = (month: string): string => {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/** Per-month tooltip: clicks, conversions, conversion rate %, earnings $. */
const MonthTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: AffiliateDashboardMonth }>;
}> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const m = payload[0].payload;
  const rate = m.clicks > 0 ? ((m.conversions / m.clicks) * 100).toFixed(1) : '0';
  return (
    <div className="affil-dash-tooltip">
      <div className="affil-dash-tooltip__title">{formatMonthLong(m.month)}</div>
      <div>Clicks: <strong>{m.clicks}</strong></div>
      <div>Conversions: <strong>{m.conversions}</strong></div>
      <div>Conversion rate: <strong>{rate}%</strong></div>
      <div>Earnings: <strong>{formatCents(m.earningsCents)}</strong></div>
    </div>
  );
};

const isNotEnrolledError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const err = error as { status?: number | string; data?: { error?: string } };
  return err.status === 403 && err.data?.error === 'NOT_ENROLLED';
};

const AffiliateDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const username = useSelector((state: RootState) => state.auth.user?.username);

  const { data: meData } = useGetMeQuery();
  const { data, isLoading, error } = useGetAffiliateDashboardQuery();

  // Non-enrolled guard → the enrollment surface. Two signals: isAffiliate on
  // /users/me (active-status only) and the endpoint's exact 403 NOT_ENROLLED
  // body. Deliberately NO redirect while either query is still loading —
  // redirecting on "not loaded yet" is the AdminRoute hard-load bug.
  const notEnrolled =
    (meData !== undefined && meData.isAffiliate !== true) || isNotEnrolledError(error);

  useEffect(() => {
    if (notEnrolled) {
      navigate('/settings/partners');
    }
  }, [notEnrolled, navigate]);

  if (notEnrolled) return null;

  if (isLoading || !data) {
    return (
      <div className="affil-dash">
        <h1 className="affil-dash-title">Partner Dashboard</h1>
        <div className="affil-dash-loading">Loading…</div>
      </div>
    );
  }

  const { months, totals } = data;
  const isZeroActivity = totals.allTimeClicks === 0 && totals.allTimeConversions === 0;
  const referralUrl = username ? getReferralUrl(username) : '';

  const handleCopyLink = async () => {
    try {
      if (await copyToClipboard(referralUrl)) toast.success('Copied!');
      else toast.error('Failed to copy link');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="affil-dash">
      <div className="affil-dash-header">
        <h1 className="affil-dash-title">Partner Dashboard</h1>
        {/* Referral link, Settings-placement style: readonly field + copy icon.
            Same getReferralUrl string everywhere — never re-derived. */}
        <div className="affil-dash-referral">
          <input
            className="affil-dash-referral__input"
            type="text"
            value={referralUrl}
            readOnly
            aria-label="Your referral link"
            onFocus={(e) => e.target.select()}
          />
          <button
            className="affil-dash-referral__copy"
            onClick={handleCopyLink}
            title="Copy referral link"
            aria-label="Copy referral link"
            type="button"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>
      </div>

      {/* Stat cards — the four Settings-tile values (identical semantics and
          numbers; parity is a backend guarantee) + the two dashboard-only
          additions (All-Time Clicks, All-Time Conversion Rate). */}
      <div className="affil-dash-cards">
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">Current Month Earnings</span>
          <span className="affil-dash-card__value">
            {formatCents(totals.currentMonthEarningsCents)}
          </span>
        </div>
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">All-Time Earnings</span>
          <span className="affil-dash-card__value">
            {formatCents(totals.allTimeEarningsCents)}
          </span>
        </div>
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">Total Conversions</span>
          <span className="affil-dash-card__value">{totals.allTimeConversions}</span>
        </div>
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">Last Payout</span>
          {totals.lastPayout ? (
            <span className="affil-dash-card__value">
              {formatCents(totals.lastPayout.amountCents)}
              <span className="affil-dash-card__subvalue">
                {formatDate(totals.lastPayout.paidAt)}
              </span>
            </span>
          ) : (
            <span className="affil-dash-card__value affil-dash-card__value--muted">
              No payouts yet
            </span>
          )}
        </div>
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">All-Time Clicks</span>
          <span className="affil-dash-card__value">{totals.allTimeClicks}</span>
        </div>
        <div className="affil-dash-card">
          <span className="affil-dash-card__label">All-Time Conversion Rate</span>
          <span className="affil-dash-card__value">
            {(totals.allTimeConversionRate * 100).toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Performance chart — clicks as bars (left axis), conversions as a
          line (right axis), the endpoint's fixed last-12-UTC-months window.
          Zero-activity affiliates get the referral-link prompt instead. */}
      <div className="affil-dash-chart-card">
        <div className="affil-dash-chart-header">
          <h2>Monthly Performance</h2>
          <p className="affil-dash-chart-subtitle">
            Clicks and conversions over the last 12 months
          </p>
        </div>
        {isZeroActivity ? (
          <div className="affil-dash-empty">
            <p className="affil-dash-empty__headline">No clicks yet — share your link!</p>
            <p className="affil-dash-empty__body">
              Your performance chart will appear here once people start clicking your
              referral link.
            </p>
            <div className="affil-dash-empty__link-row">
              <input
                className="affil-dash-empty__link"
                type="text"
                readOnly
                value={referralUrl}
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className="affil-dash-empty__copy"
                onClick={handleCopyLink}
                title="Copy referral link"
              >
                <FontAwesomeIcon icon={faCopy} /> Copy
              </button>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={months} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonthTick}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                yAxisId="clicks"
                orientation="left"
                allowDecimals={false}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <YAxis
                yAxisId="conversions"
                orientation="right"
                allowDecimals={false}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <Tooltip content={<MonthTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar
                yAxisId="clicks"
                dataKey="clicks"
                name="Clicks"
                fill="#6F51E4"
                radius={[3, 3, 0, 0]}
                maxBarSize={36}
              />
              <Line
                yAxisId="conversions"
                type="monotone"
                dataKey="conversions"
                name="Conversions"
                stroke="#E02CDC"
                strokeWidth={2}
                dot={{ r: 3, fill: '#E02CDC' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {!isZeroActivity && (
          <div className="affil-dash-legend">
            <span className="affil-dash-legend__item">
              <span className="affil-dash-legend__dot affil-dash-legend__dot--clicks" />
              Clicks
            </span>
            <span className="affil-dash-legend__item">
              <span className="affil-dash-legend__dot affil-dash-legend__dot--conversions" />
              Conversions
            </span>
          </div>
        )}
      </div>

      {/* The SAME shared component Settings > Affiliates renders — no fork. */}
      <EarningsHistoryTable />

      <PromoResources />
    </div>
  );
};

export default AffiliateDashboardPage;
